import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  useCategories,
  useCategoryTree,
  type CategoryNode,
} from '../../hooks/useCategories';
import { getCategoryDescendants } from '../../services/categoryService';

interface CategoryMultiSelectProps {
  selectedIds: string[];
  onSelect: (categoryIds: string[]) => void;
  onClear: () => void;
}

interface FlatCategory {
  id: string;
  name: string;
  color: string;
  icon?: string;
  parentName: string | null;
  hasChildren: boolean;
  depth: number;
}

function flattenForSearch(
  nodes: CategoryNode[],
  parentName: string | null = null,
  depth = 0
): FlatCategory[] {
  const result: FlatCategory[] = [];
  for (const node of nodes) {
    const hasChildren = node.children.length > 0;
    result.push({
      id: node.id,
      name: node.name,
      color: node.color,
      icon: node.icon,
      parentName,
      hasChildren,
      depth,
    });
    if (hasChildren) {
      result.push(...flattenForSearch(node.children, node.name, depth + 1));
    }
  }
  return result;
}

function flattenCategories(
  nodes: CategoryNode[],
  depth = 0
): Array<{ node: CategoryNode; depth: number }> {
  const result: Array<{ node: CategoryNode; depth: number }> = [];
  for (const node of nodes) {
    result.push({ node, depth });
    if (node.children.length > 0) {
      result.push(...flattenCategories(node.children, depth + 1));
    }
  }
  return result;
}

export function CategoryMultiSelect({
  selectedIds,
  onSelect,
  onClear,
}: CategoryMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const categoryTree = useCategoryTree();
  const categories = useCategories();

  const flatCategories = useMemo(
    () => flattenCategories(categoryTree),
    [categoryTree]
  );

  const searchableCategories = useMemo(
    () => flattenForSearch(categoryTree),
    [categoryTree]
  );

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const query = searchQuery.toLowerCase();
    return searchableCategories.filter(
      (cat) =>
        cat.name.toLowerCase().includes(query) ||
        (cat.parentName && cat.parentName.toLowerCase().includes(query))
    );
  }, [searchableCategories, searchQuery]);

  const getChildIds = useCallback(
    (categoryId: string): string[] => {
      const descendants = getCategoryDescendants(categories, categoryId);
      return descendants.map((d) => d.id);
    },
    [categories]
  );

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const isParentSelected = useCallback(
    (categoryId: string): boolean => {
      let current = categories.find((c) => c.id === categoryId);
      while (current?.parentId) {
        if (selectedIdSet.has(current.parentId)) return true;
        current = categories.find((c) => c.id === current!.parentId);
      }
      return false;
    },
    [categories, selectedIdSet]
  );

  const hasChildrenSelected = useCallback(
    (categoryId: string): boolean => {
      const childIds = getChildIds(categoryId);
      return childIds.some((id) => selectedIds.includes(id));
    },
    [getChildIds, selectedIds]
  );

  const isDisabled = useCallback(
    (categoryId: string): boolean => {
      return isParentSelected(categoryId) || hasChildrenSelected(categoryId);
    },
    [isParentSelected, hasChildrenSelected]
  );

  const handleToggle = useCallback(
    (categoryId: string) => {
      if (isDisabled(categoryId)) return;

      const isCurrentlySelected = selectedIds.includes(categoryId);
      const category = flatCategories.find((c) => c.node.id === categoryId);
      const hasChildren =
        category?.node.children && category.node.children.length > 0;

      if (isCurrentlySelected) {
        onSelect(selectedIds.filter((id) => id !== categoryId));
      } else {
        if (hasChildren) {
          const childIds = getChildIds(categoryId);
          const filteredIds = selectedIds.filter(
            (id) => !childIds.includes(id)
          );
          onSelect([...filteredIds, categoryId]);
        } else {
          onSelect([...selectedIds, categoryId]);
        }
      }
    },
    [selectedIds, onSelect, flatCategories, getChildIds, isDisabled]
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (filteredCategories && filteredCategories.length > 0) {
      setHighlightedId(filteredCategories[0].id);
    } else if (filteredCategories && filteredCategories.length === 0) {
      setHighlightedId(null);
    }
  }, [filteredCategories]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleMouseEnter = useCallback((categoryId: string) => {
    setHighlightedId(categoryId);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }

      if (event.key === 'Enter' && highlightedId) {
        event.preventDefault();
        handleToggle(highlightedId);
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const list = filteredCategories ?? searchableCategories;
        if (list.length === 0) return;

        const currentIndex = list.findIndex((c) => c.id === highlightedId);
        let nextIndex: number;

        if (event.key === 'ArrowDown') {
          nextIndex = currentIndex < list.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : list.length - 1;
        }

        const nextId = list[nextIndex].id;
        setHighlightedId(nextId);

        const element = listRef.current?.querySelector(
          `[data-category-id="${nextId}"]`
        );
        element?.scrollIntoView({ block: 'nearest' });
      }
    },
    [highlightedId, filteredCategories, searchableCategories, handleToggle]
  );

  const buttonLabel = useMemo(() => {
    if (selectedIds.length === 0) return 'Todas las categorías';
    if (selectedIds.length === 1) {
      const cat = flatCategories.find((c) => c.node.id === selectedIds[0]);
      return cat?.node.name ?? '1 categoría';
    }
    return `${selectedIds.length} categorías`;
  }, [selectedIds, flatCategories]);

  const renderCategoryOption = (
    node: CategoryNode,
    depth: number,
    isFiltered: boolean,
    parentName: string | null = null
  ) => {
    const isSelected = selectedIds.includes(node.id);
    const disabled = isDisabled(node.id);
    const isHighlighted = node.id === highlightedId;
    const paddingLeft = depth * 16 + 12;
    const hasChildren = node.children.length > 0;

    return (
      <button
        key={node.id}
        type="button"
        onClick={() => !disabled && handleToggle(node.id)}
        onMouseEnter={() => handleMouseEnter(node.id)}
        disabled={disabled}
        className={`w-full text-left px-3 py-2 flex items-center gap-2 min-h-[44px] ${
          isHighlighted && !disabled
            ? 'bg-blue-100'
            : isSelected
              ? 'bg-blue-50'
              : 'hover:bg-gray-100'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${
          hasChildren && !isFiltered ? 'font-medium' : ''
        }`}
        style={{ paddingLeft: isFiltered ? undefined : `${paddingLeft}px` }}
        role="option"
        aria-selected={isSelected}
        aria-disabled={disabled}
        data-category-id={node.id}
      >
        <input
          type="checkbox"
          checked={isSelected}
          disabled={disabled}
          readOnly
          className="w-4 h-4 text-brand-primary rounded pointer-events-none"
        />
        {depth > 0 && !isFiltered && <span className="text-gray-400">└</span>}
        <span
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: node.color }}
        />
        {node.icon && <span>{node.icon}</span>}
        <span className="truncate flex-1">{node.name}</span>
        {isFiltered && parentName && (
          <span className="text-xs text-gray-400 truncate">{parentName}</span>
        )}
        {hasChildren && (
          <span className="text-xs text-gray-400 ml-1">
            ({node.children.length})
          </span>
        )}
      </button>
    );
  };

  const renderFilteredOption = (cat: FlatCategory) => {
    const node = flatCategories.find((c) => c.node.id === cat.id)?.node;
    if (!node) return null;
    return renderCategoryOption(node, 0, true, cat.parentName);
  };

  return (
    <div className="flex flex-col gap-2 w-full sm:w-auto">
      <label className="text-sm font-medium text-mid-gray">Categorías:</label>
      <div className="relative w-full sm:w-auto" ref={containerRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full sm:w-auto px-4 py-2 border border-mid-gray rounded bg-white text-near-black min-h-[44px] flex items-center justify-between sm:justify-start gap-2"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="truncate">{buttonLabel}</span>
          <span className="text-xs flex-shrink-0">▼</span>
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 sm:right-auto mt-1 w-full sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            {/* w-full on mobile, w-80 (320px) on sm+ provides sufficient width for category names with indentation and search */}
            <div className="p-2 border-b border-gray-100">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Buscar categoría..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                aria-label="Buscar categoría"
              />
            </div>
            <div className="p-2 border-b border-gray-100 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const rootCategoryIds = categoryTree.map((c) => c.id);
                  onSelect(rootCategoryIds);
                }}
                className="text-sm text-brand-primary hover:underline"
              >
                Seleccionar todas
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={onClear}
                className="text-sm text-brand-primary hover:underline"
              >
                Deseleccionar todas
              </button>
            </div>
            <div
              ref={listRef}
              className="max-h-64 overflow-y-auto"
              role="listbox"
              aria-multiselectable="true"
            >
              {filteredCategories ? (
                filteredCategories.length > 0 ? (
                  filteredCategories.map((cat) => renderFilteredOption(cat))
                ) : (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    No se encontraron categorías
                  </div>
                )
              ) : (
                flatCategories.map(({ node, depth }) =>
                  renderCategoryOption(node, depth, false)
                )
              )}
            </div>
            <div className="p-2 border-t border-gray-100 text-xs text-gray-500">
              Selecciona una categoría padre o subcategorías, no ambas
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
